<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('cashInBank.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('cashInBank.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <ts-loading-banner :loading="loading">
      <ts-card>
        <ts-card-wrapper>
          <div class="ts-space-x-3 ts-flex ts-items-end">
            <div style="width: 100px">
              <label>{{ $t('cashInBank.companyName') }}</label>
              <div class="ts-w-full">
                <a-select
                  v-model="model.company_name"
                  :options="companyType"
                  placeholder="All"
                  class="ts-w-full"
                  @change="onCompanyNameChange"
                ></a-select>
              </div>
            </div>
            <div style="width: 250px">
              <label class="required">{{ $t('cashInBank.dateRange') }}</label>
              <div class="ts-w-full">
                <a-range-picker
                  v-model="model.date_range"
                  :allowClear="false"
                  format="DD-MM-YYYY"
                  valueFormat="DD-MM-YYYY"
                />
              </div>
            </div>
            <div style="width: 350px">
              <label>{{ $t('cashInBank.branch') }}</label>
              <div class="ts-w-full">
                <a-select
                  v-model="model.branch_id"
                  :maxTagCount="1"
                  mode="multiple"
                  :options="branches"
                  placeholder="All"
                  @change="fetchLocation"
                  showSearch
                  :filterOption="
                    (input, option) =>
                      option.componentOptions.children[0].text
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                  "
                  class="ts-w-full"
                ></a-select>
              </div>
            </div>
            <div style="width: 300px">
              <label>{{ $t('cashInBank.location') }}</label>
              <div class="ts-w-full">
                <a-select
                  v-model="model.location_id"
                  :options="warehouse"
                  :maxTagCount="1"
                  mode="multiple"
                  placeholder="All"
                  showSearch
                  :filterOption="
                    (input, option) =>
                      option.componentOptions.children[0].text
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                  "
                  class="ts-w-full"
                ></a-select>
              </div>
            </div>
            <div
              style="width: 100px; height: 30px"
              class="ts-flex ts-space-x-3"
            >
              <ts-button @click.prevent="fetchData" color="primary">{{
                $t('preview')
              }}</ts-button>
              <ts-button @click.prevent="exportExcel" color="info" outline>{{
                $t('excel')
              }}</ts-button>
            </div>
          </div>
        </ts-card-wrapper>
        <hr />
        <ts-card-wrapper>
          <div class="row">
            <div class="col-md-8">
              <table class="ts-w-full table">
                <thead>
                  <tr>
                    <th
                      class="ts-bg-yellow-500 ts-text-center ts-text-black ts-py-1 ts-px-2"
                    >
                      {{ $t('cashInBank.totalSaleInvoice') }}
                    </th>
                    <th
                      class="ts-bg-yellow-500 ts-text-center ts-text-black ts-py-1 ts-px-2"
                    >
                      {{ $t('cashInBank.totalCashForward') }}
                    </th>
                    <th
                      class="ts-bg-yellow-500 ts-text-center ts-text-black ts-py-1 ts-px-2"
                    >
                      {{ $t('cashInBank.totalSaleReceipt') }}
                    </th>
                    <th
                      class="ts-bg-yellow-500 ts-text-center ts-text-black ts-py-1 ts-px-2"
                    >
                      {{ $t('cashInBank.totalPaidByCash') }}
                    </th>
                    <th
                      class="ts-bg-yellow-500 ts-text-center ts-text-black ts-py-1 ts-px-2"
                    >
                      {{ $t('cashInBank.totalCashInBank') }}
                    </th>
                    <th
                      class="ts-bg-yellow-500 ts-text-center ts-text-black ts-py-1 ts-px-2"
                    >
                      {{ $t('cashInBank.totalOutStandingBalance') }}
                    </th>
                    <th
                      class="ts-bg-yellow-500 ts-text-center ts-text-black ts-py-1 ts-px-2"
                    >
                      {{ $t('cashInBank.totalSaleCreditFromAllMonth') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="ts-border ts-text-center">
                      {{
                        $formatNumber(total_invoice_amount, default_precision)
                      }}
                    </td>
                    <td class="ts-border ts-text-center">
                      {{
                        $formatNumber(
                          total_sale_invoice_pending,
                          default_precision
                        )
                      }}
                    </td>
                    <td class="ts-border ts-text-center">
                      {{ $formatNumber(total_sale_receipt, default_precision) }}
                    </td>
                    <td class="ts-border ts-text-center">
                      {{ $formatNumber(total_paid_by_cash, default_precision) }}
                    </td>
                    <td class="ts-border ts-text-center">
                      {{ $formatNumber(total_cash_in_bank, default_precision) }}
                    </td>
                    <td class="ts-border ts-text-center">
                      {{
                        $formatNumber(
                          total_outstanding_balance,
                          default_precision
                        )
                      }}
                    </td>
                    <td class="ts-border ts-text-center">
                      {{
                        $formatNumber(
                          total_sale_credit_from_all_month,
                          default_precision
                        )
                      }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="col-md-4">
              <table class="table">
                <tr v-for="(bank, name) in groupByBank" :key="name">
                  <td
                    class="ts-border ts-text-black ts-py-1 ts-px-2 ts-bg-yellow-500"
                  >
                    <strong class="ts-uppercase">
                      {{ bank.bank_name.replaceAll('_', ' ') }}
                    </strong>
                  </td>
                  <td class="ts-border ts-py-1 ts-px-2 ts-text-center">
                    {{ $formatNumber(bank.total, default_precision) }}
                  </td>
                </tr>
              </table>
            </div>
          </div>
          <div class="row">
            <div class="col-md-12">
              <div
                class="ts-overflow-auto"
                style="max-height: 500px; min-height: 300px"
              >
                <table class="table">
                  <thead>
                    <tr>
                      <th
                        class="ts-bg-yellow-500 ts-sticky ts-top-0 ts-z-10 ts-text-center ts-text-black ts-py-1 ts-px-2"
                      >
                        {{ $t('no') }}
                      </th>
                      <th
                        class="ts-bg-yellow-500 ts-sticky ts-top-0 ts-z-10 ts-text-center ts-text-black ts-py-1 ts-px-2"
                      >
                        {{ $t('cashInBank.invoice#') }}
                      </th>
                      <th
                        class="ts-bg-yellow-500 ts-sticky ts-top-0 ts-z-10 ts-text-center ts-text-black ts-py-1 ts-px-2"
                      >
                        {{ $t('cashInBank.invoiceDate') }}
                      </th>
                      <th
                        class="ts-bg-yellow-500 ts-sticky ts-top-0 ts-z-10 ts-text-center ts-text-black ts-py-1 ts-px-2"
                      >
                        {{ $t('cashInBank.saleInvoice') }}
                      </th>
                      <th
                        class="ts-bg-yellow-500 ts-sticky ts-top-0 ts-z-10 ts-text-center ts-text-black ts-py-1 ts-px-2"
                        v-for="(bank, index) in bankByCompany"
                        :key="index"
                      >
                        {{ bank.replaceAll('_', ' ') }}
                      </th>
                      <th
                        class="ts-bg-yellow-500 ts-sticky ts-top-0 ts-z-10 ts-text-center ts-text-black ts-py-1 ts-px-2"
                      >
                        {{ $t('cashInBank.saleReceipt') }}
                      </th>
                      <th
                        class="ts-bg-yellow-500 ts-sticky ts-top-0 ts-z-10 ts-text-center ts-text-black ts-py-1 ts-px-2"
                      >
                        {{ $t('cashInBank.paidByCash') }}
                      </th>
                      <th
                        class="ts-bg-yellow-500 ts-sticky ts-top-0 ts-z-10 ts-text-center ts-text-black ts-py-1 ts-px-2"
                      >
                        {{ $t('cashInBank.cashInBank') }}
                      </th>
                      <th
                        class="ts-bg-yellow-500 ts-sticky ts-top-0 ts-z-10 ts-text-center ts-text-black ts-py-1 ts-px-2"
                        style="width: 10%;"
                      >
                        {{ $t('cashInBank.cashShortOrOverByTransaction') }}
                      </th>
                      <th
                        class="ts-bg-yellow-500 ts-sticky ts-top-0 ts-z-10 ts-text-center ts-text-black ts-py-1 ts-px-2"
                      >
                        {{ $t('cashInBank.outStandingBalance') }}
                      </th>
                      <th
                        class="ts-bg-yellow-500 ts-sticky ts-top-0 ts-z-10 ts-text-center ts-text-black ts-py-1 ts-px-2"
                      >
                        {{ $t('cashInBank.description') }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, index) in resources" :key="index">
                      <td class="ts-border ts-text-center">{{ index + 1 }}</td>
                      <td
                        class="ts-border ts-text-center ts-whitespace-no-wrap"
                      >
                        {{ item.invoice_no }}
                      </td>
                      <td
                        class="ts-border ts-text-center ts-whitespace-no-wrap"
                      >
                        {{ item.stock_out_date }}
                      </td>
                      <td class="ts-border ts-text-center">
                        {{
                          $formatNumber(item.invoice_amount, default_precision)
                        }}
                      </td>
                      <td
                        class="ts-border ts-text-center"
                        v-for="(bank, index) in bankByCompany"
                        :key="index"
                      >
                        {{ $formatNumber(item[bank], default_precision) }}
                      </td>

                      <td class="ts-border ts-text-center">
                        {{
                          $formatNumber(item.sale_receipt, default_precision)
                        }}
                      </td>
                      <td class="ts-border ts-text-center">
                        {{
                          $formatNumber(item.paid_by_cash, default_precision)
                        }}
                      </td>
                      <td class="ts-border ts-text-center">
                        {{
                          $formatNumber(item.cash_in_bank, default_precision)
                        }}
                      </td>
                      <td class="ts-border ts-text-center">
                        {{
                          $formatNumber(
                            item.cash_short_over_by_transaction,
                            default_precision
                          )
                        }}
                      </td>
                      <td class="ts-border ts-text-center">
                        {{
                          $formatNumber(
                            item.outstanding_balance,
                            default_precision
                          )
                        }}
                      </td>
                      <td class="ts-border ts-text-center">
                        {{ item.description }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </ts-card-wrapper>
      </ts-card>
    </ts-loading-banner>
  </div>
</template>
<script>
import moment from 'moment'
import { mapActions } from 'vuex'
import { sumBy, groupBy, trim } from 'lodash'
import { Errors } from 'form-backend-validation'
import * as FileDownload from 'downloadjs'

export default {
  name: 'cashInBank',
  data() {
    return {
      errors: new Errors(),
      loading: false,
      model: {
        branch_id: undefined,
        location_id: undefined,
        date_range: [
          moment()
            .startOf('month')
            .format('DD-MM-YYYY'),
          moment()
            .endOf('month')
            .format('DD-MM-YYYY')
        ],
        company_name: 'meta'
      },
      branches: [],
      warehouse: [],
      banks: [],
      resources: [],
      total_invoice_amount: 0,
      total_sale_invoice_pending: 0,
      total_sale_receipt: 0,
      total_cash_in_bank: 0,
      total_paid_by_cash: 0,
      total_outstanding_balance: 0,
      total_sale_credit_from_all_month: 0
    }
  },
  computed: {
    sumByColumn: vm => column => {
      return sumBy(vm.resources, column)
    },
    sumByColumnBank: () => resource => {
      return sumBy(resource, 'cash_in_bank')
    },
    groupByBank() {
      // return groupBy(this.resources, 'bank_name')
      let totals = {}
      if (this.resources.length > 0) {
        this.resources.forEach(item => {
          for (let bankName in item) {
            if (this.bankByCompany.includes(bankName)) {
              if (totals[bankName]) {
                totals[bankName] += item[bankName]
              } else {
                totals[bankName] = item[bankName]
              }
            }
          }
        })

        return Object.entries(totals).map(([bankName, total]) => ({
          bank_name: bankName,
          total
        }))
      }
      return this.bankByCompany.map(el => ({
        bank_name: el,
        total: 0
      }))
    },
    companyType() {
      return [
        { value: 'meta', label: 'Meta' },
        { value: 'gm88', label: 'GM88' }
      ]
    },
    bankByCompany() {
      return this.banks
    },
    default_precision() {
      return this.$store.state.authUser.sys_setting.decimal_digit
        ? this.$store.state.authUser.sys_setting.decimal_digit
        : 4
    }
  },
  methods: {
    ...mapActions('report/finance', ['cashInBank', 'cashInBankExcel']),
    ...mapActions('report/resource', ['formView']),
    onCompanyNameChange() {
      this.model.location_id = undefined
      this.fetchLocation()
    },
    fetchResource() {
      this.loading = true
      this.formView({
        fnName: 'branchAssigned,bankAccountByCompanyBranch',
        company_name: this.model.company_name
      })
        .then(response => {
          this.branches = response.data['branchAssigned'].map(el => ({
            value: el.branch_id,
            label: el.branch_name_en
          }))
          this.banks = response.data['bankAccountByCompanyBranch'].map(
            el => el.account_name
          )
        })
        .finally(() => (this.loading = false))
    },
    fetchLocation() {
      this.loading = true
      this.formView({
        fnName: 'locationByCompanyBranch,bankAccountByCompanyBranch',
        branch_id: this.model.branch_id,
        company_name: this.model.company_name
      })
        .then(response => {
          this.warehouse = response.data['locationByCompanyBranch'].map(el => ({
            value: el.location_id,
            label: el.location_name_en
          }))
          this.banks = response.data['bankAccountByCompanyBranch'].map(
            el => el.account_name
          )
        })
        .finally(() => (this.loading = false))
    },
    fetchData() {
      this.loading = true
      this.cashInBank(this.model)
        .then(response => {
          this.resources = response.data.data
          this.total_invoice_amount = response.data.total_invoice_amount
          this.total_sale_invoice_pending =
            response.data.total_sale_invoice_pending
          this.total_sale_receipt = response.data.total_sale_receipt
          this.total_paid_by_cash = response.data.total_paid_by_cash
          this.total_cash_in_bank = response.data.total_cash_in_bank
          this.total_outstanding_balance =
            response.data.total_outstanding_balance
          this.total_sale_credit_from_all_month =
            response.data.total_sale_credit_from_all_month
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.loading = false
        })
    },
    exportExcel() {
      this.loading = true
      this.errors = new Errors()
      this.cashInBankExcel(this.model)
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.loading = false))
    }
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.fetchResource()
    })
  }
}
</script>
<style scoped>
.table thead th {
  vertical-align: top;
}
</style>
